import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Alert, Stack, Chip,
} from '@mui/material';
import { Print, PictureAsPdf, GridOn, Refresh, AccountBalance } from '@mui/icons-material';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { reportBankAnalysisURL } from '../../BaseURL';

function headers() { return { Authorization: `Bearer ${localStorage.getItem('church_token')}` }; }

const fmt = (n) =>
  Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtSigned = (n) => {
  const v = Number(n || 0);
  const s = Math.abs(v).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s})` : s;
};

const fmtDateLong = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'long', year: 'numeric' });
};

const NAVY  = '#0B2545';
const GREEN = '#0F766E';
const RED   = '#B91C1C';
const GOLD  = '#C89211';
const RULE  = '#0B2545';

export default function BankAnalysis({ start, end }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const printRef = useRef(null);

  const load = () => {
    if (!start || !end) return;
    setLoading(true); setError('');
    axios.get(reportBankAnalysisURL, {
      headers: headers(),
      params: { start, end },
    })
      .then(r => { if (r.data.success) setData(r.data); else setError(r.data.message); })
      .catch(err => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  // ─── PRINT ────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!printRef.current) return;
    const w = window.open('', '_blank', 'width=1200,height=820');
    if (!w) return;
    w.document.write(`
      <html><head><title>Bank Analysis - ${data?.period.label || ''}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: 'Helvetica Neue', -apple-system, Segoe UI, Roboto, Arial, sans-serif; margin: 28px 32px; color: #111; }
        .letterhead { display: flex; align-items: center; gap: 20px; padding-bottom: 14px; border-bottom: 3px double ${NAVY}; }
        .letterhead img { width: 84px; height: 84px; object-fit: contain; border-radius: 6px; }
        .letterhead .info h1 { margin: 0; font-size: 26px; letter-spacing: 0.02em; color: ${NAVY}; }
        .letterhead .info p { margin: 3px 0 0; color: #555; font-size: 12px; }
        .stmt-title { text-align: center; margin: 22px 0 4px; }
        .stmt-title .name { font-size: 15px; font-weight: 800; letter-spacing: 0.16em; color: ${NAVY}; border-top: 1.5px solid ${NAVY}; border-bottom: 1.5px solid ${NAVY}; display: inline-block; padding: 4px 24px; }
        .stmt-title .period { margin-top: 8px; font-size: 12px; color: #555; }
        table { width: 100%; border-collapse: collapse; margin-top: 16px; }
        thead th { text-align: left; font-size: 10.5px; letter-spacing: 0.06em; text-transform: uppercase; color: #fff; background: ${NAVY}; padding: 9px 10px; }
        thead th.right { text-align: right; }
        tbody td { padding: 8px 10px; font-size: 12.5px; border-bottom: 1px solid #eef0f4; }
        tbody tr:nth-child(even) td { background: #fafbfc; }
        tbody td.right { text-align: right; font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .grand td { border-top: 3px double ${NAVY}; border-bottom: 3px double ${NAVY}; font-weight: 800; font-size: 13px; background: #FFF5DA; padding: 12px 10px; text-transform: uppercase; letter-spacing: 0.08em; color: #6B4A08; }
        .footer { display: flex; justify-content: space-between; margin-top: 30px; font-size: 10px; color: #888; }
        @page { size: A4 landscape; margin: 12mm; }
      </style>
      </head><body>${printRef.current.innerHTML}</body></html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 300);
  };

  // ─── EXPORT PDF ───────────────────────────────────────────────────────────
  const handlePdf = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 36;

    let y = 32;
    const logoSize = 52;
    let textLeft = margin;
    if (data.church?.logo && String(data.church.logo).startsWith('data:image')) {
      try {
        const fmtType = data.church.logo.substring(11, data.church.logo.indexOf(';'));
        doc.addImage(data.church.logo, fmtType.toUpperCase(), margin, y, logoSize, logoSize);
        textLeft = margin + logoSize + 12;
      } catch (e) { /* skip */ }
    }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(11, 37, 69);
    doc.text(data.church?.name || 'Church', textLeft, y + 18);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(90);
    const subInfo = [data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ');
    if (subInfo) doc.text(subInfo, textLeft, y + 32);
    y += Math.max(logoSize, 36) + 6;
    doc.setDrawColor(11, 37, 69); doc.setLineWidth(1.0);
    doc.line(margin, y, pageW - margin, y);
    doc.setLineWidth(0.4); doc.line(margin, y + 3, pageW - margin, y + 3);
    y += 16;

    // Title
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(11, 37, 69);
    const title = 'BANK ANALYSIS';
    const titleW = doc.getTextWidth(title);
    const tx = (pageW - titleW) / 2;
    doc.setLineWidth(0.6);
    doc.line(tx - 16, y - 4, tx + titleW + 16, y - 4);
    doc.text(title, pageW / 2, y + 10, { align: 'center' });
    doc.line(tx - 16, y + 15, tx + titleW + 15, y + 15);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(90);
    doc.text(
      `For the period ${fmtDateLong(data.period.start_date)} to ${fmtDateLong(data.period.end_date)}`,
      pageW / 2, y + 30, { align: 'center' }
    );
    y += 46;

    const body = data.accounts.map((a, i) => ([
      String(i + 1),
      a.name,
      { content: fmt(a.opening),                styles: { halign: 'right' } },
      { content: a.deposits    ? fmt(a.deposits)    : '—', styles: { halign: 'right', textColor: a.deposits    ? [15, 118, 110] : [148, 163, 184] } },
      { content: a.withdrawals ? fmt(a.withdrawals) : '—', styles: { halign: 'right', textColor: a.withdrawals ? [185, 28, 28]  : [148, 163, 184] } },
      { content: fmtSigned(a.net_movement), styles: { halign: 'right', textColor: a.net_movement >= 0 ? [15, 118, 110] : [185, 28, 28], fontStyle: 'bold' } },
      { content: fmt(a.closing), styles: { halign: 'right', fontStyle: 'bold', textColor: [11, 37, 69] } },
    ]));

    body.push([
      { content: `GRAND TOTAL — ${data.accounts.length} account${data.accounts.length === 1 ? '' : 's'}`,
        colSpan: 2, styles: { fontStyle: 'bold', fillColor: [255, 245, 218], textColor: [107, 74, 8], fontSize: 12 } },
      { content: fmt(data.totals.opening),
        styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 245, 218], textColor: [107, 74, 8], fontSize: 12 } },
      { content: fmt(data.totals.deposits),
        styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 245, 218], textColor: [15, 118, 110], fontSize: 12 } },
      { content: fmt(data.totals.withdrawals),
        styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 245, 218], textColor: [185, 28, 28], fontSize: 12 } },
      { content: fmtSigned(data.totals.net_movement),
        styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 245, 218],
          textColor: data.totals.net_movement >= 0 ? [15, 118, 110] : [185, 28, 28], fontSize: 12 } },
      { content: fmt(data.totals.closing),
        styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 245, 218], textColor: [107, 74, 8], fontSize: 12 } },
    ]);

    autoTable(doc, {
      startY: y,
      head: [[
        '#',
        'Bank Account',
        { content: 'Balance B/fwd (GH₵)',  styles: { halign: 'right' } },
        { content: 'Deposits (GH₵)',       styles: { halign: 'right' } },
        { content: 'Withdrawals (GH₵)',    styles: { halign: 'right' } },
        { content: 'Net Movement (GH₵)',   styles: { halign: 'right' } },
        { content: 'Balance C/fwd (GH₵)',  styles: { halign: 'right' } },
      ]],
      body,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 5, lineColor: [230, 232, 238], lineWidth: 0.4 },
      headStyles: { fillColor: [11, 37, 69], textColor: 255, fontStyle: 'bold', halign: 'left', valign: 'middle' },
      alternateRowStyles: { fillColor: [250, 251, 252] },
      columnStyles: {
        0: { cellWidth: 26, halign: 'center' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 100, halign: 'right' },
        3: { cellWidth: 92, halign: 'right' },
        4: { cellWidth: 100, halign: 'right' },
        5: { cellWidth: 100, halign: 'right' },
        6: { cellWidth: 100, halign: 'right' },
      },
      margin: { top: 40, left: margin, right: margin, bottom: 40 },
      didDrawPage: () => {
        doc.setFontSize(8); doc.setTextColor(120);
        doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageW - margin, pageH - 18, { align: 'right' });
        doc.text(`Generated ${new Date().toLocaleString('en-GH')}`, margin, pageH - 18);
      },
    });

    const stamp = `${data.period.start_date}_to_${data.period.end_date}`;
    doc.save(`Bank_Analysis_${stamp.replace(/[\/\s]/g, '_')}.pdf`);
  };

  // ─── EXPORT EXCEL ─────────────────────────────────────────────────────────
  const handleExcel = () => {
    if (!data) return;
    const aoa = [];
    aoa.push([data.church?.name || 'Church']);
    if (data.church?.address) aoa.push([data.church.address]);
    const contact = [data.church?.phone, data.church?.email].filter(Boolean).join(' · ');
    if (contact) aoa.push([contact]);
    aoa.push([]);
    aoa.push(['BANK ANALYSIS']);
    aoa.push([`For the period ${fmtDateLong(data.period.start_date)} to ${fmtDateLong(data.period.end_date)}`]);
    aoa.push([]);
    aoa.push(['#', 'Bank Account', 'Status', 'Balance B/fwd (GH₵)', 'Deposits (GH₵)', 'Deposit Count',
              'Withdrawals (GH₵)', 'Withdrawal Count', 'Net Movement (GH₵)', 'Balance C/fwd (GH₵)']);
    data.accounts.forEach((a, i) => {
      aoa.push([
        i + 1, a.name, a.status,
        Number(a.opening),
        Number(a.deposits), a.deposit_count,
        Number(a.withdrawals), a.withdrawal_count,
        Number(a.net_movement),
        Number(a.closing),
      ]);
    });
    aoa.push(['', 'GRAND TOTAL', '',
      Number(data.totals.opening),
      Number(data.totals.deposits), '',
      Number(data.totals.withdrawals), '',
      Number(data.totals.net_movement),
      Number(data.totals.closing),
    ]);

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [
      { wch: 5 }, { wch: 32 }, { wch: 10 }, { wch: 16 }, { wch: 16 }, { wch: 12 },
      { wch: 18 }, { wch: 14 }, { wch: 16 }, { wch: 18 },
    ];
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (const C of [3, 4, 6, 8, 9]) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[addr];
        if (cell && typeof cell.v === 'number') cell.z = '#,##0.00';
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bank Analysis');
    const stamp = `${data.period.start_date}_to_${data.period.end_date}`;
    XLSX.writeFile(wb, `Bank_Analysis_${stamp.replace(/[\/\s]/g, '_')}.xlsx`);
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Building bank analysis for {fmtDateLong(start)} — {fmtDateLong(end)}…
        </Typography>
      </Box>
    );
  }
  if (error) return <Alert severity="error" onClose={() => setError('')}>{error}</Alert>;
  if (!data) return null;

  return (
    <Box>
      {/* Action bar */}
      <Stack direction="row" spacing={1} justifyContent="flex-end" mb={2} flexWrap="wrap">
        <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
        <Button size="small" variant="outlined" startIcon={<Print />} onClick={handlePrint} disabled={data.accounts.length === 0}>Print</Button>
        <Button size="small" variant="outlined" startIcon={<GridOn />} onClick={handleExcel} disabled={data.accounts.length === 0}
          sx={{ color: '#0F6C3D', borderColor: '#0F6C3D', '&:hover': { borderColor: '#0F6C3D', bgcolor: '#0F6C3D0d' } }}>
          Export Excel
        </Button>
        <Button size="small" variant="contained" startIcon={<PictureAsPdf />} onClick={handlePdf} disabled={data.accounts.length === 0}
          sx={{ bgcolor: RED, '&:hover': { bgcolor: '#991B1B' } }}>
          Export PDF
        </Button>
      </Stack>

      {/* Statement paper */}
      <Paper
        elevation={2}
        sx={{
          border: '1px solid #E2E8F0', borderRadius: 2,
          p: { xs: 2, md: 4 }, bgcolor: '#fff',
          maxWidth: 1280, mx: 'auto',
          boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
        }}
      >
        <Box ref={printRef}>
          {/* Letterhead */}
          <Box className="letterhead" sx={{
            display: 'flex', alignItems: 'center', gap: 3,
            pb: 2, borderBottom: `3px double ${RULE}`,
          }}>
            {data.church?.logo ? (
              <Box component="img" src={data.church.logo} alt="Logo"
                sx={{ width: 84, height: 84, objectFit: 'contain', borderRadius: 1 }} />
            ) : (
              <Box sx={{
                width: 84, height: 84, borderRadius: 1,
                bgcolor: `${NAVY}12`, color: NAVY,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 28,
              }}>
                {(data.church?.name || 'C').charAt(0).toUpperCase()}
              </Box>
            )}
            <Box className="info" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" fontWeight={800}
                sx={{ color: NAVY, letterSpacing: '0.02em', lineHeight: 1.1 }}>
                {data.church?.name || 'Church'}
              </Typography>
              {(data.church?.address || data.church?.phone || data.church?.email) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {[data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ')}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Title */}
          <Box className="stmt-title" sx={{ textAlign: 'center', mt: 3, mb: 1.5 }}>
            <Typography className="name" sx={{
              fontSize: 15, fontWeight: 800, letterSpacing: '0.16em', color: NAVY,
              borderTop: `1.5px solid ${NAVY}`, borderBottom: `1.5px solid ${NAVY}`,
              display: 'inline-block', px: 3, py: 0.5,
            }}>
              BANK ANALYSIS
            </Typography>
            <Typography className="period" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              For the period <b>{fmtDateLong(data.period.start_date)}</b> to <b>{fmtDateLong(data.period.end_date)}</b>
            </Typography>
            {data.accounts.length > 0 && (
              <Stack direction="row" spacing={1} justifyContent="center" mt={1} flexWrap="wrap">
                <Chip size="small" label={`${data.accounts.length} account${data.accounts.length === 1 ? '' : 's'}`}
                  sx={{ bgcolor: `${NAVY}15`, color: NAVY, fontWeight: 700 }} />
                <Chip size="small" label={`${data.totals.tx_count} transaction${data.totals.tx_count === 1 ? '' : 's'}`}
                  sx={{ bgcolor: `${GOLD}20`, color: '#6B4A08', fontWeight: 700 }} />
                <Chip size="small" label={`Closing GH₵ ${fmt(data.totals.closing)}`}
                  sx={{ bgcolor: `${GREEN}15`, color: GREEN, fontWeight: 700, fontFamily: 'monospace' }} />
              </Stack>
            )}
          </Box>

          {/* Body */}
          {data.accounts.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
              <AccountBalance sx={{ fontSize: 40, color: '#c9d3e0', mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={700}>No bank accounts set up</Typography>
              <Typography variant="body2">Add a bank account first to see this analysis.</Typography>
            </Box>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14 }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 36 }}>#</th>
                  <th style={thStyle}>Bank Account</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Balance B/fwd (GH₵)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Deposits (GH₵)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Withdrawals (GH₵)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Net Movement (GH₵)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Balance C/fwd (GH₵)</th>
                </tr>
              </thead>
              <tbody>
                {data.accounts.map((a, i) => (
                  <tr key={a.id} style={{ background: i % 2 === 1 ? '#fafbfc' : 'transparent' }}>
                    <td style={{ ...cellStyle, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                    <td style={{ ...cellStyle, fontWeight: 700, color: NAVY }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountBalance sx={{ fontSize: 16, color: NAVY }} />
                        <span>{a.name}</span>
                        {a.status !== 'active' && (
                          <span style={{
                            display: 'inline-block', padding: '1px 6px',
                            borderRadius: 8, background: '#eee', color: '#666',
                            fontSize: 10, fontWeight: 700, textTransform: 'capitalize',
                          }}>{a.status}</span>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 10.5 }}>
                        {a.deposit_count} dep · {a.withdrawal_count} pmt
                      </Typography>
                    </td>
                    <td style={{ ...amountStyle, color: NAVY }}>{fmt(a.opening)}</td>
                    <td style={{ ...amountStyle, color: a.deposits ? GREEN : '#cbd5e1', fontWeight: a.deposits ? 700 : 400 }}>
                      {a.deposits ? fmt(a.deposits) : '—'}
                    </td>
                    <td style={{ ...amountStyle, color: a.withdrawals ? RED : '#cbd5e1', fontWeight: a.withdrawals ? 700 : 400 }}>
                      {a.withdrawals ? fmt(a.withdrawals) : '—'}
                    </td>
                    <td style={{ ...amountStyle, fontWeight: 800, color: a.net_movement >= 0 ? GREEN : RED }}>
                      {fmtSigned(a.net_movement)}
                    </td>
                    <td style={{ ...amountStyle, fontWeight: 800, color: NAVY }}>
                      {fmt(a.closing)}
                    </td>
                  </tr>
                ))}

                {/* Grand total */}
                <tr>
                  <td colSpan={2} style={grandLabelStyle}>
                    GRAND TOTAL — {data.accounts.length} account{data.accounts.length === 1 ? '' : 's'}
                  </td>
                  <td style={grandAmountStyle}>{fmt(data.totals.opening)}</td>
                  <td style={{ ...grandAmountStyle, color: GREEN }}>{fmt(data.totals.deposits)}</td>
                  <td style={{ ...grandAmountStyle, color: RED }}>{fmt(data.totals.withdrawals)}</td>
                  <td style={{ ...grandAmountStyle, color: data.totals.net_movement >= 0 ? GREEN : RED }}>
                    {fmtSigned(data.totals.net_movement)}
                  </td>
                  <td style={grandAmountStyle}>{fmt(data.totals.closing)}</td>
                </tr>
              </tbody>
            </table>
          )}

          {/* Footer */}
          <Box className="footer" sx={{
            display: 'flex', justifyContent: 'space-between', mt: 4,
            fontSize: 10, color: '#888',
          }}>
            <span>Generated {new Date().toLocaleString('en-GH')}</span>
            <span>{data.church?.name || ''}</span>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

// ─── Inline styles ─────────────────────────────────────────────────────────
const thStyle = {
  textAlign: 'left', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: '#fff', background: NAVY, padding: '10px 10px', fontWeight: 700,
};
const cellStyle    = { padding: '10px 10px', fontSize: 13, borderBottom: '1px solid #eef0f4', color: '#1e293b', verticalAlign: 'top' };
const amountStyle  = { padding: '10px 10px', fontSize: 13, borderBottom: '1px solid #eef0f4',
                       textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                       fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', verticalAlign: 'top' };
const grandLabelStyle = {
  padding: '14px 10px', fontSize: 13, fontWeight: 800, background: '#FFF5DA',
  color: '#6B4A08', borderTop: `3px double ${NAVY}`, borderBottom: `3px double ${NAVY}`,
  textTransform: 'uppercase', letterSpacing: '0.08em',
};
const grandAmountStyle = {
  padding: '14px 10px', fontSize: 14, fontWeight: 800, textAlign: 'right',
  background: '#FFF5DA', color: '#6B4A08',
  borderTop: `3px double ${NAVY}`, borderBottom: `3px double ${NAVY}`,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};
